// Diary.js - Diary section layout
//
// This used to render its own Tabs (Calendar View / Group View / Expenses /
// History / Working Days Config) and switch between child views with local
// tab state. That navigation has moved into the sidebar (see the "Diary"
// group in src/lib/navigation.js) so each view is now its own route,
// registered as nested routes under /management/diary in App.js.
//
// This component is now just the shared Diary header/card shell: it shows
// the "Diary Management" title (and, only on the Calendar page, the month
// navigation), and renders whichever sub-page is active via <Outlet />.
// The Calendar page reads `currentDate` back out via useOutletContext()
// since the month navigation controls live up here in the shared header.
import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import {
  CalendarToday,
  Today,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const Diary = () => {
  const theme = useTheme();
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());

  // Month navigation only makes sense on the Calendar page.
  const isCalendarRoute =
    location.pathname === '/management/diary' ||
    location.pathname === '/management/diary/calendar';

  const handleCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  // Only the Calendar page keeps the shared card shell. Every other Diary
  // page (Groups, Expenses, History, Working Days) is built in the new
  // Tailwind style with its own cards, and relies on the app shell for
  // padding/background - so wrapping it in another card with its own
  // background, shadow and inner scroll made it look inset and double-scrolled.
  if (!isCalendarRoute) {
    return <Outlet context={{ currentDate }} />;
  }

  return (
   <Box sx={{
  width: '100%',
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}}>
    

      {/* Active Diary Page (Calendar / Groups / Expenses / History / Working Days) */}
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          pb: { xs: 8, sm: 6, md: 4 },
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: (theme) => theme.palette.mode === 'dark' ? '#555' : '#c1c1c1',
            borderRadius: '10px',
            '&:hover': {
              background: (theme) => theme.palette.mode === 'dark' ? '#777' : '#a8a8a8',
            },
          },
        }}>
          <Outlet context={{ currentDate }} />
        </Box>
      </Box>
    </Box>
  );
};

export default Diary;